import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { isNumber } from 'lodash';

import {
  PILCROW_CHARACTER,
  PILCROW_REGEX,
  MAX_TEXT_LENGTH,
} from '../../../../../../constants';
import { onScrollDownMessage } from '../../utils';

function TextInput(props) {
  const {
    minLength,
    maxLength: maxChars,
    rowId,
    rowRefId,
    rowOrder,
    columnId,
    columnRefId,
    columnOrder,
    required,
    inputStyle,
    rows = 3,
  } = props;

  const maxLength = isNumber(maxChars) ? maxChars : MAX_TEXT_LENGTH;
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  const onChange = useCallback((event) => {
    setValue(event.target.value);
  }, []);

  const onKeyDown = useCallback((event) => {
    if (event.keyCode === 13 && inputRef.current) {
      inputRef.current.blur();
    }
  }, []);

  const onKeyUp = useCallback(
    (event) => {
      if (event.key === PILCROW_CHARACTER) {
        setValue(value.replace(PILCROW_REGEX, ''));
      }
    },
    [value],
  );

  const onPaste = useCallback((event) => event.preventDefault(), []);

  const onClick = useCallback((event) => {
    event.preventDefault();
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const onBlur = useCallback((e) => {
    if (e.target.value !== '') {
      e.target.parentElement.classList.remove(
        'answer--matrix-open-ended-answer-input-container-required',
      );
    }

    const questionWrapper = Pollfish.ui.getClosestQuestionWrapperElement(
      e.target,
    );
    const isDisplaySamePage = Pollfish.ui.isQuestionDisplaySamePage(
      questionWrapper,
    );

    if (!isDisplaySamePage) {
      onScrollDownMessage();
    }

    Pollfish.ui.validateQuestionAnswers(questionWrapper);
  }, []);

  const valueLength = value.length;
  const remaining = maxLength - valueLength;
  // we decided we don't want to show negative values on the remaining number
  const sanitizedRemaining = remaining >= 0 ? remaining : 0;
  const isZeroRemaining = sanitizedRemaining === 0;

  let className = 'input-text-note--valid';

  if (isNumber(minLength) && valueLength < minLength) {
    className = 'input-text-note--invalid';
  }

  if (isZeroRemaining) {
    className = 'input-text-note--invalid';
  }

  return (
    <div className="flex flex-align-items--center flex-column padding-none">
      <div className="answer--matrix-open-ended-answer-input-container input-text-container form-container">
        <textarea
          id={`freeText-${rowRefId}-${columnRefId}`}
          ref={inputRef}
          data-row-id={rowId}
          data-row-ref-id={rowRefId}
          data-row-order={rowOrder}
          data-column-id={columnId}
          data-column-ref-id={columnRefId}
          data-column-order={columnOrder}
          data-input-required={required}
          className={`input-text answer--matrix-open-ended-answer-input ${inputStyle}`}
          rows={rows}
          maxLength={maxLength}
          onClick={onClick}
          onChange={onChange}
          onKeyUp={onKeyUp}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={onBlur}
          value={value}
          aria-label={`numeric value for row: ${rowOrder} and column: ${columnOrder}`}
        ></textarea>
      </div>
      <span
        className={`input-text-note ${className}`}
        style={{ padding: '4px 7px', width: '100%' }}
      >
        <span>{sanitizedRemaining}</span>
      </span>
    </div>
  );
}

TextInput.propTypes = {
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  rowId: PropTypes.number,
  rowRefId: PropTypes.string,
  rowOrder: PropTypes.number,
  columnId: PropTypes.number,
  columnRefId: PropTypes.string,
  columnOrder: PropTypes.number,
  required: PropTypes.bool,
  inputStyle: PropTypes.string,
  rows: PropTypes.number,
};

export default TextInput;
